import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  InputAdornment,
  Drawer,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Avatar,
  Badge,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Fingerprint as FingerprintIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  Phone as PhoneIcon,
  Archive as ArchiveIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { getAllClients } from '../../services/firebase';
import { Client } from '../../types';
import { formatDate, formatPhoneNumber } from '../../utils/formatters';
import ClientFilter, { FilterOptions } from '../../components/ClientFilter';
import { filterClients } from '../../utils/filters';
import { useAuth } from '../../contexts/AuthContext';

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'country' | 'activeCases' | 'applicationDate';
type ViewMode = 'table' | 'grid';

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || first.toUpperCase();
};

const avatarColorFor = (name: string): string => {
  const palette = ['#1E3A5F', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#14B8A6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const computeActiveCases = (c: Client) =>
  c.cases.filter((k) => k.status === 'Active').length;

const computeApplicationDate = (c: Client): Date | null => {
  if (!c.cases || c.cases.length === 0) return null;
  const sorted = [...c.cases].sort(
    (a, b) =>
      new Date(a.applicationDate).getTime() - new Date(b.applicationDate).getTime(),
  );
  return new Date(sorted[0].applicationDate);
};

const hasFingerprintsTaken = (c: Client) => c.cases.some((k) => k.biometricTaken);

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser, role } = useAuth();
  const ownerFilter = role === 'staff' ? currentUser?.uid : undefined;

  const [allClients, setAllClients] = useState<Client[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; clientId: string } | null>(
    null,
  );

  const [filters, setFilters] = useState<FilterOptions>({
    dateRange: 'all',
    biometricStatus: 'all',
    archiveStatus: 'active',
    country: 'all',
    customStartDate: undefined,
    customEndDate: undefined,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAllClients(false, ownerFilter);
        if (cancelled) return;
        setAllClients(data);
        setClients(data);
      } catch (err) {
        console.error('ClientList: Error loading clients:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerFilter]);

  useEffect(() => {
    if (allClients.length > 0) {
      setClients(filterClients(allClients, filters));
    }
  }, [allClients, filters]);

  const filterCount =
    (filters.dateRange !== 'all' ? 1 : 0) +
    (filters.biometricStatus !== 'all' ? 1 : 0) +
    (filters.archiveStatus !== 'active' ? 1 : 0);

  const visibleClients = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    const matches = clients.filter(
      (c) =>
        c.fullName.toLowerCase().includes(lower) ||
        c.email.toLowerCase().includes(lower) ||
        c.phoneNumber.includes(searchTerm) ||
        (typeof c.countryOfOrigin === 'string' &&
          c.countryOfOrigin.toLowerCase().includes(lower)),
    );

    const sorted = [...matches].sort((a, b) => {
      const cmp = (x: Client, y: Client) => {
        switch (orderBy) {
          case 'name':
            return x.fullName.localeCompare(y.fullName);
          case 'country':
            return String(x.countryOfOrigin || '').localeCompare(
              String(y.countryOfOrigin || ''),
            );
          case 'activeCases':
            return computeActiveCases(x) - computeActiveCases(y);
          case 'applicationDate': {
            const xd = computeApplicationDate(x);
            const yd = computeApplicationDate(y);
            if (!xd && !yd) return 0;
            if (!xd) return 1;
            if (!yd) return -1;
            return xd.getTime() - yd.getTime();
          }
          default:
            return 0;
        }
      };
      return order === 'asc' ? cmp(a, b) : cmp(b, a);
    });

    return sorted;
  }, [clients, searchTerm, order, orderBy]);

  const handleSort = (col: OrderBy) => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const pageRows = visibleClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const stickyFirstHead = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 3,
    backgroundColor: '#F9FAFB',
  };
  const stickyFirstBody = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 1,
    backgroundColor: 'inherit',
  };

  const TableSkeleton = () => (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i} sx={{ '& td': { height: 56 } }}>
          <TableCell sx={stickyFirstBody}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={180} height={18} />
            </Box>
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={120} height={16} />
          </TableCell>
          <TableCell>
            <Skeleton variant="rounded" width={64} height={22} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={90} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="rounded" width={96} height={28} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const GridSkeleton = () => (
    <Grid container spacing={3}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Grid item xs={12} sm={6} lg={4} key={i}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="80%" />
                </Box>
              </Box>
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="65%" />
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Skeleton variant="rounded" width={70} height={22} />
                <Skeleton variant="rounded" width={90} height={22} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const EmptyState: React.FC<{ searched: boolean }> = ({ searched }) => (
    <Box
      sx={{
        textAlign: 'center',
        py: { xs: 12, md: 16 },
        px: 4,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: '#EFF6FF',
          color: '#3B82F6',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <PersonOutlineIcon sx={{ fontSize: 32 }} />
      </Box>
      <Typography
        sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
      >
        {searched ? 'No clients found' : 'No clients yet'}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 3 }}>
        {searched
          ? 'Try adjusting your search or filters.'
          : 'Add your first client to get started.'}
      </Typography>
      {!searched && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/add-client')}
        >
          Add Client
        </Button>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 5, md: 6 },
      }}
    >
      {/* Page header */}
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 3,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
          >
            Clients
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Manage your client records and cases
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            startIcon={<FilterListIcon />}
            onClick={() => setFilterDrawerOpen(true)}
            variant="outlined"
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Filters
            {filterCount > 0 && (
              <Chip
                label={filterCount}
                size="small"
                color="primary"
                sx={{
                  ml: 1,
                  height: 18,
                  minWidth: 18,
                  '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                }}
              />
            )}
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={() => navigate('/add-client')}
            variant="contained"
            color="primary"
            sx={{ flex: { xs: 1, sm: 'none' } }}
          >
            Add Client
          </Button>
        </Box>
      </Box>

      {/* Toolbar */}
      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: { xs: 4, sm: 5 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search by name, email, phone…"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', md: 360 } }}
            inputProps={{ 'aria-label': 'Search clients' }}
          />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, v) => v && setViewMode(v)}
            size="small"
            sx={{
              alignSelf: { xs: 'flex-end', md: 'auto' },
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
                px: 1.5,
                py: 0.75,
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: '#EFF6FF',
                  color: '#1D4ED8',
                  borderColor: '#BFDBFE',
                  '&:hover': { bgcolor: '#DBEAFE' },
                },
              },
            }}
          >
            <ToggleButton value="table" aria-label="Table view">
              <ViewListIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton value="grid" aria-label="Grid view">
              <GridViewIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Body */}
        {viewMode === 'table' ? (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 720, tableLayout: 'fixed' }} aria-label="Clients">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyFirstHead, width: '40%' }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '22%' }}>
                      <TableSortLabel
                        active={orderBy === 'country'}
                        direction={orderBy === 'country' ? order : 'asc'}
                        onClick={() => handleSort('country')}
                      >
                        Country
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>
                      <TableSortLabel
                        active={orderBy === 'activeCases'}
                        direction={orderBy === 'activeCases' ? order : 'asc'}
                        onClick={() => handleSort('activeCases')}
                      >
                        Cases
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '14%' }}>
                      <TableSortLabel
                        active={orderBy === 'applicationDate'}
                        direction={orderBy === 'applicationDate' ? order : 'asc'}
                        onClick={() => handleSort('applicationDate')}
                      >
                        Applied
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ width: 140 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableSkeleton />
                  ) : visibleClients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, borderBottom: 'none' }}>
                        <EmptyState searched={!!searchTerm || filterCount > 0} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageRows.map((client, idx) => {
                      const fp = hasFingerprintsTaken(client);
                      const initials = initialsFromName(client.fullName);
                      const color = avatarColorFor(client.fullName);
                      const rowBg = idx % 2 === 1 ? '#F9FAFB' : '#FFFFFF';
                      const active = computeActiveCases(client);
                      const appDate = computeApplicationDate(client);
                      return (
                        <TableRow
                          key={client.id}
                          hover
                          sx={{
                            backgroundColor: { xs: '#FFFFFF', md: rowBg },
                            '& td': { height: 56 },
                            '&:hover .row-actions': { opacity: 1 },
                            '@media (hover: none)': {
                              '& .row-actions': { opacity: 1 },
                            },
                          }}
                        >
                          <TableCell sx={stickyFirstBody}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                invisible={!fp}
                                badgeContent={
                                  <Box
                                    sx={{
                                      width: 14,
                                      height: 14,
                                      borderRadius: '50%',
                                      bgcolor: '#10B981',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '2px solid #FFFFFF',
                                    }}
                                    aria-label="Fingerprints taken"
                                  >
                                    <FingerprintIcon
                                      sx={{ fontSize: 8, color: '#FFFFFF' }}
                                    />
                                  </Box>
                                }
                              >
                                <Avatar
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: color,
                                    fontSize: '0.75rem',
                                    color: '#FFFFFF',
                                  }}
                                >
                                  {initials}
                                </Avatar>
                              </Badge>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {client.fullName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatPhoneNumber(client.phoneNumber) || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {client.countryOfOrigin || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={active}
                              size="small"
                              sx={{
                                bgcolor: active > 0 ? '#EFF6FF' : '#F3F4F6',
                                color: active > 0 ? '#1D4ED8' : '#6B7280',
                                fontWeight: 600,
                                minWidth: 32,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                            {appDate ? formatDate(appDate) : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              className="row-actions"
                              sx={{
                                display: 'inline-flex',
                                gap: 0.25,
                                opacity: { xs: 1, md: 0 },
                                transition: 'opacity 150ms ease',
                              }}
                            >
                              <Tooltip title="View" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/client/${client.id}`)}
                                  aria-label={`View ${client.fullName}`}
                                >
                                  <VisibilityIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/client/${client.id}`)}
                                  aria-label={`Edit ${client.fullName}`}
                                >
                                  <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="More actions" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) =>
                                    setActionMenu({
                                      anchor: e.currentTarget,
                                      clientId: client.id,
                                    })
                                  }
                                  aria-label={`More actions for ${client.fullName}`}
                                >
                                  <MoreVertIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && visibleClients.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={visibleClients.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows:"
                labelDisplayedRows={({ from, to, count }) =>
                  `Showing ${count === 0 ? 0 : from}–${to} of ${count} ${
                    count === 1 ? 'client' : 'clients'
                  }`
                }
              />
            )}
          </>
        ) : (
          <Box sx={{ p: { xs: 4, sm: 5 } }}>
            {loading ? (
              <GridSkeleton />
            ) : visibleClients.length === 0 ? (
              <EmptyState searched={!!searchTerm || filterCount > 0} />
            ) : (
              <Grid container spacing={3}>
                {visibleClients.map((client) => {
                  const fp = hasFingerprintsTaken(client);
                  const initials = initialsFromName(client.fullName);
                  const color = avatarColorFor(client.fullName);
                  const active = computeActiveCases(client);
                  return (
                    <Grid item xs={12} sm={6} lg={4} key={client.id}>
                      <Card
                        onClick={() => navigate(`/client/${client.id}`)}
                        sx={{
                          height: '100%',
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: 'divider',
                          transition:
                            'border-color 150ms ease, box-shadow 150ms ease, transform 100ms ease',
                          '&:hover': {
                            borderColor: '#3B82F6',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          },
                          '&:active': { transform: 'scale(0.995)' },
                          '&:focus-visible': {
                            outline: '2px solid #3B82F6',
                            outlineOffset: 2,
                          },
                        }}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/client/${client.id}`);
                          }
                        }}
                      >
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              mb: 3,
                            }}
                          >
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              invisible={!fp}
                              badgeContent={
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    bgcolor: '#10B981',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '2px solid #FFFFFF',
                                  }}
                                >
                                  <FingerprintIcon
                                    sx={{ fontSize: 9, color: '#FFFFFF' }}
                                  />
                                </Box>
                              }
                            >
                              <Avatar
                                sx={{
                                  width: 44,
                                  height: 44,
                                  bgcolor: color,
                                  fontSize: '0.95rem',
                                  color: '#FFFFFF',
                                }}
                              >
                                {initials}
                              </Avatar>
                            </Badge>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.9375rem',
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {client.fullName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {client.countryOfOrigin || '—'}
                              </Typography>
                            </Box>
                          </Box>

                          <Stack spacing={1} sx={{ mb: 3 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                color: 'text.secondary',
                              }}
                            >
                              <PhoneIcon sx={{ fontSize: 14 }} />
                              <Typography sx={{ fontSize: '0.8125rem' }}>
                                {formatPhoneNumber(client.phoneNumber)}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Chip
                                label={`${active} ${active === 1 ? 'case' : 'cases'}`}
                                size="small"
                                sx={{
                                  bgcolor: active > 0 ? '#EFF6FF' : '#F3F4F6',
                                  color: active > 0 ? '#1D4ED8' : '#6B7280',
                                  fontWeight: 600,
                                }}
                              />
                            </Box>
                          </Stack>

                          <Box
                            sx={{
                              pt: 3,
                              borderTop: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                color: '#3B82F6',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              View Profile <span aria-hidden>→</span>
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}
      </Paper>

      {/* Row action menu */}
      <Menu
        anchorEl={actionMenu?.anchor ?? null}
        open={Boolean(actionMenu)}
        onClose={() => setActionMenu(null)}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: 180,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (actionMenu) navigate(`/client/${actionMenu.clientId}`);
            setActionMenu(null);
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5 }}
        >
          <VisibilityIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          View Profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenu) navigate(`/client/${actionMenu.clientId}`);
            setActionMenu(null);
          }}
          sx={{ fontSize: '0.875rem', gap: 1.5 }}
        >
          <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          Edit Details
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => setActionMenu(null)}
          sx={{ fontSize: '0.875rem', gap: 1.5, color: 'text.secondary' }}
        >
          <ArchiveIcon sx={{ fontSize: 18 }} />
          Archive Client
        </MenuItem>
      </Menu>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: isXs ? '100%' : 400,
            p: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            boxShadow: '-8px 0 24px rgba(0,0,0,0.06)',
          },
        }}
      >
        <ClientFilter
          filters={filters}
          onFiltersChange={(f) => {
            setFilters(f);
            setPage(0);
          }}
          onClose={() => setFilterDrawerOpen(false)}
          onApply={() => setFilterDrawerOpen(false)}
          showActiveFilters
          totalCount={allClients.length}
          visibleCount={visibleClients.length}
          countryOptions={Array.from(
            new Set(
              allClients
                .map((c) => String(c.countryOfOrigin || ''))
                .filter(Boolean),
            ),
          )}
        />
      </Drawer>
    </Box>
  );
};

export default ClientList;
